/**
 * AiReviewBatchConfirmModal — 批量 AI 审查确认弹层（波次 2）。
 *
 * 文档库多选条「批量 AI 审查」的确认步骤：左列选中文档清单（发起前最后一次
 * 核对，超出可视高度滚动），审查立场 stance 选填——透传 doc-review skill 的
 * 自由文本立场（留空即默认「中立（不偏向任何一方）」，不作为枚举收窄）。
 * 提交 POST /documents/ai-review-batches（202 受理），成功后由调用方打开
 * 批详情抽屉跟进进度。
 */

import { useState } from "react";
import { List, Modal, Input, Typography, message } from "antd";
import { useCreateAiReviewBatch } from "../../api/queries";
import { mapApiError } from "../../utils/api-error";

const { Text } = Typography;

/** 确认层只需要文档的展示子集，避免与完整 Document 类型耦合。 */
export interface BatchReviewDocumentPreview {
  id: string;
  title: string;
}

interface AiReviewBatchConfirmModalProps {
  open: boolean;
  documents: BatchReviewDocumentPreview[];
  onCancel: () => void;
  /** 提交受理成功（202 只回批 id），参数为新批 id。 */
  onSubmitted: (batchId: string) => void;
}

export default function AiReviewBatchConfirmModal({
  open,
  documents,
  onCancel,
  onSubmitted,
}: AiReviewBatchConfirmModalProps) {
  const [messageApi, contextHolder] = message.useMessage();
  const [stance, setStance] = useState("");
  const createMut = useCreateAiReviewBatch();

  const handleSubmit = async () => {
    if (documents.length === 0) return;
    try {
      const batch = await createMut.mutateAsync({
        documentIds: documents.map((d) => d.id),
        stance: stance.trim() || null,
      });
      messageApi.success(`已受理批量审查（${documents.length} 份），进度见右侧抽屉`);
      setStance("");
      onSubmitted(batch.id);
    } catch (err) {
      messageApi.error(mapApiError(err));
    }
  };

  return (
    <Modal
      title="批量 AI 审查"
      open={open}
      onCancel={() => {
        if (createMut.isPending) return;
        onCancel();
      }}
      onOk={handleSubmit}
      okText={`审查 ${documents.length} 份`}
      cancelText="取消"
      confirmLoading={createMut.isPending}
      okButtonProps={{ disabled: documents.length === 0 }}
      width={560}
      destroyOnHidden
      data-testid="ai-review-batch-confirm-modal"
    >
      {contextHolder}
      <List
        size="small"
        header={<Text type="secondary">将审查以下 {documents.length} 份文档</Text>}
        dataSource={documents}
        style={{
          maxHeight: 260,
          overflowY: "auto",
          marginBottom: "var(--jerry-sp-3)",
        }}
        renderItem={(doc) => (
          <List.Item key={doc.id} data-testid={`batch-confirm-doc-${doc.id}`}>
            <Text ellipsis style={{ maxWidth: "100%" }}>
              {doc.title}
            </Text>
          </List.Item>
        )}
      />
      <Input
        placeholder="审查立场（选填，默认中立，如：站在我方立场）"
        value={stance}
        onChange={(e) => setStance(e.target.value)}
        maxLength={200}
        disabled={createMut.isPending}
        data-testid="batch-confirm-stance-input"
      />
    </Modal>
  );
}
